import { createModel } from '@rematch/core';
import Axios from 'axios';
import $ from 'jquery';
import { format } from 'date-fns-tz';
import { SupportedCultures, QuoteStatuses } from '../../constants';
import getLocaleFromCulture from './getLocaleFromCulture';
import currentDate from '../../currentDate';

export type QuoteItem = {
  itemId: string;
  name: string;
  description: string;
  quantity: number;
  price: number;
  um: string | 'each';
  productCode: string | '';
};

type QuoteState = {
  id: string | null;
  useFortisPayment: boolean;
  html: string;
  error: boolean;
  tax: number;
  items: QuoteItem[];
  attachments: string[];
  projectName: string;
  dealerName: string;
  dealerId: string;
  title: string;
  status?: string;
  responderName?: string;
  updatedDate?: Date;
  sentDate?: Date;
  culture: string;
  zipFileName?: string;
  isCurrencyPayDisable: boolean;
  isSignatureDisable?: boolean;
  serverNotificationDone: boolean;
  paymentStatus: string | null;
  paymentStatusUpdatedDate?: Date;
  wasCurrencyConverted: boolean | null;
  total: number;
  freightTotal: number;
};

const defaultState: QuoteState = {
  id: null,
  useFortisPayment: false,
  html: '',
  error: false,
  total: 0,
  freightTotal: 0,
  items: [],
  attachments: [],
  projectName: '',
  dealerName: '',
  dealerId: '',
  title: '',
  status: undefined,
  responderName: undefined,
  updatedDate: undefined,
  culture: 'en-US',
  zipFileName: undefined,
  isCurrencyPayDisable: true,
  isSignatureDisable: true,
  serverNotificationDone: false,
  paymentStatus: 'undefined',
  paymentStatusUpdatedDate: undefined,
  wasCurrencyConverted: false,
  tax: 0
};

const setQuoteServerNotificationDone = function handleSetQuoteServerNotificationDone(
  state: QuoteState,
  serverNotificationDone: boolean
): QuoteState {
  return { ...state, serverNotificationDone };
};

const setQuoteId = function handleSetQuoteId(
  state: QuoteState,
  id: string
): QuoteState {
  return {
    ...defaultState,
    id
  };
};

const setUseFortisPayment = function handleSetUseFortisPayment(
  state: QuoteState,
  useFortisPayment: boolean
): QuoteState {
  return { ...state, useFortisPayment };
};

const setQuoteHtml = function handleSetQuoteHtml(
  state: QuoteState,
  html: string
): QuoteState {
  return { ...state, html };
};

const setQuoteError = function handleSetQuoteError(
  state: QuoteState,
  error: boolean
): QuoteState {
  return { ...state, error };
};

const setQuoteItems = function handleSetQuoteItems(
  state: QuoteState,
  items: QuoteItem[]
): QuoteState {
  return { ...state, items };
};

const setQuoteAttachments = function handleSetQuoteAttachments(
  state: QuoteState,
  attachments: string[]
): QuoteState {
  return { ...state, attachments };
};

const setQuoteProjectName = function handleSetQuoteProjectName(
  state: QuoteState,
  projectName: string
): QuoteState {
  return { ...state, projectName };
};

const setQuoteDealerName = function handleSetQuoteDealerName(
  state: QuoteState,
  dealerName: string
): QuoteState {
  return { ...state, dealerName };
};

const setQuoteDealerId = function handleSetQuoteDealerId(
  state: QuoteState,
  dealerId: string
): QuoteState {
  return { ...state, dealerId };
};

const setQuoteTitle = function handleSetQuoteTitle(
  state: QuoteState,
  title: string
): QuoteState {
  return { ...state, title };
};

const setQuoteStatus = function handleSetQuoteStatus(
  state: QuoteState,
  status: string,
  updatedDate: Date | undefined
): QuoteState {
  return { ...state, status, updatedDate };
};

const setQuoteUpdatedDate = function handleSetQuoteUpdatedDate(
  state: QuoteState,
  updatedDate: Date | undefined
): QuoteState {
  return { ...state, updatedDate };
};

const setResponderName = function handleSetResponderName(
  state: QuoteState,
  responderName: string
): QuoteState {
  return {
    ...state,
    responderName
  };
};

const setQuoteCulture = function handleSetQuoteCulture(
  state: QuoteState,
  culture: string
): QuoteState {
  return { ...state, culture };
};

const setZipFileName = function handleSetQuoteZipFileName(
  state: QuoteState,
  zipFileName: string
): QuoteState {
  return { ...state, zipFileName };
};

const setQuoteTotal = function handleSetQuoteTotal(
  state: QuoteState,
  total: number
): QuoteState {
  return { ...state, total };
};

const setFreightTotal = function handleSetFreightotal(
  state: QuoteState,
  freightTotal: number
): QuoteState {
  return { ...state, freightTotal };
};

const setIsCurrencyPayDisable = function handleSetIsCurrencyPayDisable(
  state: QuoteState,
  isCurrencyPayDisable: boolean
): QuoteState {
  return {
    ...state,
    isCurrencyPayDisable
  };
};
const setIsSignatureDisable = function handleSetIsSignatureDisable(
  state: QuoteState,
  isSignatureDisable: boolean | undefined
): QuoteState {
  return {
    ...state,
    isSignatureDisable
  };
};

const setPaymentStatus = function handleSetPaymentSTatus(
  state: QuoteState,
  paymentStatus: string,
  paymentStatusUpdatedDate: Date | undefined
): QuoteState {
  return {
    ...state,
    paymentStatus,
    paymentStatusUpdatedDate
  };
};

const setQuoteTax = function handleSetQuoteTax(
  state: QuoteState,
  tax: number
): QuoteState {
  return { ...state, tax };
};

const updatePrintedLine = function updatePrintedLine(
  element: JQuery<HTMLElement>,
  acceptedBy: string
): void {
  element
    .find('.Signature')
    .find('tr:eq(3)')
    .find('.Underline')
    .html(acceptedBy);
};

const updateSignatureLine = function updateSignatureLine(
  element: JQuery<HTMLElement>,
  acceptedBy: string
): void {
  element
    .find('.Signature')
    .find('tr:eq(1)')
    .find('.Column1')
    .find('.Underline')
    .html(`&nbsp;${acceptedBy}`);
};

const updateDateLine = function updateDateLine(
  element: JQuery<HTMLElement>,
  acceptedBy: string
): void {
  element
    .find('.Signature')
    .find('tr:eq(1)')
    .find('.Column2')
    .find('.Underline')
    .html(acceptedBy);
};

const signQuote = function handleSignQuote(state: QuoteState): QuoteState {
  const { html, responderName, updatedDate } = state;
  if (!html || !responderName || !updatedDate) {
    return state;
  }

  const el = $('<div></div>');
  el.html(state.html);
  updateSignatureLine(el, responderName);
  updatePrintedLine(el, responderName);
  const locale = getLocaleFromCulture(state.culture);
  updateDateLine(el, format(updatedDate, 'P pp zzz', { locale }));

  return { ...state, html: el.html() };
};

const getDocumentTitle = function handleGetDocumentTitle(
  projectName: string,
  title: string
): string | null {
  if (!projectName && !title) {
    return null;
  }

  if (!title) {
    return projectName;
  }

  if (!projectName) {
    return title;
  }

  return `${projectName} ${title}`;
};

const setDocumentTitle = function handleSetDocumentTitle(
  projectName: string,
  title: string
): void {
  const documentTitle = getDocumentTitle(projectName, title);
  if (documentTitle) {
    document.title = documentTitle;
  }
};

const setWasCurrencyConverted = function handleSetWasCurrencyConverted(
  state: QuoteState,
  wasCurrencyConverted: boolean
): QuoteState {
  return { ...state, wasCurrencyConverted };
};

const setSentDate = function handleSetSentDate(
  state: QuoteState,
  sentDate: Date
): QuoteState {
  return { ...state, sentDate };
};

const quote = createModel({
  state: defaultState,
  reducers: {
    setQuoteId,
    setUseFortisPayment,
    setQuoteHtml,
    setQuoteError,
    setQuoteTotal,
    setFreightTotal,
    setQuoteItems,
    setQuoteAttachments,
    setQuoteProjectName,
    setQuoteDealerName,
    setQuoteDealerId,
    setQuoteTitle,
    setQuoteStatus,
    setQuoteUpdatedDate,
    setResponderName,
    signQuote,
    setQuoteCulture,
    setZipFileName,
    setIsCurrencyPayDisable,
    setIsSignatureDisable,
    setQuoteServerNotificationDone,
    setPaymentStatus,
    setWasCurrencyConverted,
    setQuoteTax,
    setSentDate
  },
  effects: dispatch => ({
    async getQuoteDocument(quoteId: string): Promise<void> {
      try {
        const { data: html } = await Axios.get(`${quoteId}/quote.html`);
        this.setQuoteHtml(html);
      } catch {
        this.setQuoteError(true);
      }
    },
    async getQuoteRecord(quoteId: string): Promise<void> {
      this.setQuoteId(quoteId);
      try {
        const { data: info } = await Axios.get(`${quoteId}/quoteInfo.json`);

        this.setUseFortisPayment(info.useFortisPayment);
        this.setWasCurrencyConverted(info.wasCurrencyConverted);
        this.setQuoteTax(info.taxAmount);

        this.setQuoteTotal(info.total);
        this.setFreightTotal(info.freightTotal);
        this.setQuoteItems(info.items);
        if (info.specSheets) {
          this.setQuoteAttachments(info.specSheets);
        }
        this.setQuoteProjectName(info.projectName);
        this.setQuoteDealerName(info.dealerName);
        this.setQuoteDealerId(info.dealerId);
        this.setQuoteTitle(info.title);
        const paymentMethods = {
          CreditCard: info.allowCreditCard,
          BankWire: info.allowBankWire,
          ExpressFinancing: info.allowExpressFinancing
        };
        dispatch.currencyPay.setPaymentMethods(paymentMethods);
        this.setIsCurrencyPayDisable(info.isDisableCurrencyPay);
        this.setIsSignatureDisable(info.isDisableSignature);
        this.setZipFileName(info.zipFileName);
        if (SupportedCultures.includes(info.culture)) {
          this.setQuoteCulture(info.culture);
        }
        setDocumentTitle(info.projectName, info.title);

        const axiosApiInstance = Axios.create({
          baseURL: window.AutoQuotes.config.apiBaseUrl
        });
        const quoteStatusCall = axiosApiInstance.get(`quote/${quoteId}`);
        const { data: quoteInstance } = await quoteStatusCall;
        const quoteStatus =
          quoteInstance.status === 'ChangeReq'
            ? QuoteStatuses.ChangesRequested
            : quoteInstance.status;
        const quoteUpdatedDate = new Date(quoteInstance.updatedDate);
        const quotePaymentStatusUpdatedDate = new Date(
          quoteInstance.paymentStatusUpdatedDate
        );

        const quoteSentDate = new Date(quoteInstance.sentDate);

        this.setSentDate(quoteSentDate);

        if (!quoteStatus) {
          const a = Axios.create({
            baseURL: window.AutoQuotes.config.apiBaseUrl
          });
          await a.post(`quote/${quoteId}`, {
            status: QuoteStatuses.Viewed
          });
          this.setQuoteStatus(QuoteStatuses.Viewed, currentDate());
        } else {
          this.setResponderName(quoteInstance.responderName);
          this.setQuoteStatus(quoteStatus, quoteUpdatedDate);
        }

        this.setPaymentStatus(
          quoteInstance.paymentStatus,
          quotePaymentStatusUpdatedDate
        );

        this.setQuoteServerNotificationDone(true);
      } catch {
        this.setQuoteError(true);
      }
    }
  })
});

export default quote;
